import React, { createContext, useContext, useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Breadcrumb from '@Services/Breadcrumb';
import { trackShowHideConditions } from '@Pages/Results/analytics';

export const ToggleConditionsGlobalContext = createContext([]);

export function ToggleConditionsGlobalProvider({ children }) {
  const [showConditions, setShowConditions] = useState(true);
  const contextValue = useMemo(() => [showConditions, setShowConditions], [showConditions]);
  return <ToggleConditionsGlobalContext.Provider value={contextValue}>{children}</ToggleConditionsGlobalContext.Provider>;
}

ToggleConditionsGlobalProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function withToggleConditionsGlobalProvider() {
  return function withToggleConditionsGlobalHOC(Component) {
    return function withToggleConditionsGlobalComponent(props) {
      return (
        <ToggleConditionsGlobalProvider>
          <Component {...props} />
        </ToggleConditionsGlobalProvider>
      );
    };
  };
}

export function withToggleConditionsGlobalConsumer() {
  return function withToggleConditionsGlobalHOC(Component) {
    return function withToggleConditionsGlobalComponent(props) {
      return (
        <ToggleConditionsGlobalContext.Consumer>
          {([showConditions]) => <Component {...props} showConditions={showConditions} />}
        </ToggleConditionsGlobalContext.Consumer>
      );
    };
  };
}

export function useToggleConditionsGlobal({ databaseId, resultName }) {
  const [showConditions, setShowConditions] = useContext(ToggleConditionsGlobalContext);

  useEffect(() => {
    const conditionsState = Breadcrumb.getResultsStateForDb(databaseId, 'reactionConditions');
    // restore state of conditions toggle
    if (conditionsState) {
      setShowConditions(conditionsState.show);
    } else {
      // new hit set or there is no saved info, so initialize
      Breadcrumb.updateResultsStateForDb(databaseId, 'reactionConditions', {
        show: showConditions,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [databaseId]);

  const toggleConditions = useCallback(() => {
    const toggled = !showConditions;

    trackShowHideConditions(toggled);
    setShowConditions(toggled);
    Breadcrumb.updateResultsStateForDb(databaseId, 'reactionConditions', {
      resultName,
      show: toggled,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showConditions]);

  return [showConditions, toggleConditions];
}
